import { useCallback, useEffect, useRef, useState } from "react";
import {
  PremioRecebido,
  getPremiosNaoVisualizados,
  marcarPremioComoVisualizado,
} from "services/premios-recebidos.service";

interface FilaDePremios {
  premioAtual: PremioRecebido | null;
  restantes: number;
  avancar: () => void;
}

/**
 * Fila dos prêmios que o participante ainda não viu.
 *
 * O prêmio é marcado como visualizado assim que entra na tela, não ao fechar:
 * se o participante fechar o navegador no meio da fila, o prêmio já exibido não
 * volta a aparecer, e a janela em que duas abas mostrariam o mesmo prêmio fica
 * limitada ao instante da exibição.
 */
export function usePremiosRecebidos(playerId?: string): FilaDePremios {
  const [fila, setFila] = useState<PremioRecebido[]>([]);
  const playerCarregadoRef = useRef<string>();
  const marcadosRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // sem player (logout ou aviso desligado) a fila é descartada, para que um
    // novo login na mesma aba volte a consultar em vez de reusar o já carregado
    if (!playerId) {
      playerCarregadoRef.current = undefined;
      marcadosRef.current.clear();
      setFila([]);
      return;
    }

    if (playerCarregadoRef.current === playerId) return;

    playerCarregadoRef.current = playerId;
    getPremiosNaoVisualizados(playerId)
      .then(setFila)
      .catch(() => {
        // sem prêmios em tela; a fila é recarregada no próximo acesso
        playerCarregadoRef.current = undefined;
      });
  }, [playerId]);

  const premioAtual = fila.length > 0 ? fila[0] : null;

  useEffect(() => {
    if (!playerId || !premioAtual) return;
    if (marcadosRef.current.has(premioAtual._id)) return;

    marcadosRef.current.add(premioAtual._id);
    marcarPremioComoVisualizado(playerId, premioAtual).catch(() => {
      // deixa o prêmio voltar na próxima carga se a marcação não foi gravada
      marcadosRef.current.delete(premioAtual._id);
    });
  }, [playerId, premioAtual]);

  const avancar = useCallback(() => {
    setFila((atual) => atual.slice(1));
  }, []);

  return { premioAtual, restantes: fila.length, avancar };
}
